import {
  ArrowRight,
  BookOpen,
  CreditCard,
  FileText,
  Globe2,
  Search,
  ShoppingBag,
  UserRound,
} from "lucide-react";
import { useRouter, type NavigateOptions } from "@tanstack/react-router";
import {
  aiPolicyPath,
  helpHref,
  helpSearch,
  type HelpAudience,
} from "../../../src/lib/help-navigation";
import type { HelpArticlePage, HelpIndex } from "../help.server";
import { Input } from "../../components/ui-kit/forms";
import { SegmentedLinks } from "../../components/ui-kit/segmented-links";

const icons = {
  start: BookOpen,
  website: Globe2,
  payments: CreditCard,
  shop: ShoppingBag,
  account: UserRound,
  policies: FileText,
};

// Searches that each find their answer, in the words each audience tends to use.
const popularSearches = {
  creators: ["domain", "publish", "Stripe"],
  members: ["cancel", "refund", "locked"],
};

// One wording on the server and in the browser, whatever the visitor's locale or time zone.
const updatedOn = new Intl.DateTimeFormat("en-US", { dateStyle: "long", timeZone: "UTC" });

function otherAudience(audience: HelpAudience): HelpAudience {
  return audience === "creators" ? "members" : "creators";
}

function articles(count: number) {
  return `${count} ${count === 1 ? "article" : "articles"}`;
}

function HelpSearch({
  audience,
  query = "",
  compact = false,
  action = "/help",
  placeholder = "Search for an answer…",
}: {
  audience: HelpAudience;
  query?: string;
  compact?: boolean;
  action?: string;
  placeholder?: string;
}) {
  const router = useRouter();
  return (
    <form
      className="help-search"
      role="search"
      action={action}
      method="get"
      data-compact={compact || undefined}
      onSubmit={(event) => {
        // Keep searches in this document so Back restores the URL, results and input together.
        // The ordinary GET action remains the fallback before JavaScript is ready.
        event.preventDefault();
        const search = helpSearch(Object.fromEntries(new FormData(event.currentTarget)));
        void router.navigate({ href: helpHref(action, audience, search.q) } as NavigateOptions);
      }}
    >
      {audience === "members" && <input type="hidden" name="audience" value={audience} />}
      <Input
        key={`${action}:${query}`}
        id="help-search"
        name="q"
        label="Search the help center"
        hideLabel
        type="search"
        value={query}
        placeholder={placeholder}
        maxLength={160}
        enterKeyHint="search"
      />
      {/* Beside an article the search steps back, so the reading keeps the only filled button. */}
      <button className={compact ? "button button-outline" : "button button-primary"} type="submit">
        <Search size={17} aria-hidden="true" />
        <span>Search</span>
      </button>
    </form>
  );
}

function HelpContact() {
  return (
    <section className="help-contact" aria-labelledby="help-contact-title">
      <div>
        <h2 id="help-contact-title">A little help from a person.</h2>
        <p>Can’t find your answer? Tell us what you’re working on.</p>
      </div>
      <a className="button button-outline" href="mailto:support@drate.app">
        Contact support
      </a>
    </section>
  );
}

function ArticleList({
  articles,
  columns = false,
}: {
  articles: HelpIndex["results"];
  columns?: boolean;
}) {
  return (
    <ul className="help-article-list" data-columns={columns || undefined}>
      {articles.map((article) => (
        <li key={article.slug}>
          <a href={article.path}>
            <span>
              <span className="help-article-title">{article.title}</span>
              <span className="help-article-summary">{article.description}</span>
            </span>
            <ArrowRight size={18} aria-hidden="true" />
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Every topic beside a topic's articles or a search, so the next place to look is one click. */
function TopicList({ page }: { page: HelpIndex }) {
  return (
    <nav className="help-side" aria-labelledby="help-topics-title">
      <p id="help-topics-title" className="help-side-label">
        Topics
      </p>
      <ul>
        {page.categories.map((category) => (
          <li key={category.slug}>
            <a
              href={helpHref(`/help/topics/${category.slug}`, page.audience)}
              aria-current={page.category?.slug === category.slug ? "page" : undefined}
            >
              <span>{category.title}</span>
              <span className="help-count">
                {category.count}
                <span className="sr-only"> {category.count === 1 ? "article" : "articles"}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

function NoResults({ page }: { page: HelpIndex }) {
  const other = otherAudience(page.audience);
  return (
    <div className="help-empty">
      {page.query ? (
        <>
          <h3>No answers for “{page.query}” yet</h3>
          <p>
            Try fewer words or a different phrase. You can also{" "}
            {page.category && (
              <>
                <a href={helpHref("/help", page.audience, page.query)}>search every topic</a>{" "}
                or{" "}
              </>
            )}
            <a href={helpHref(page.path, other, page.query)}>search help for {other}</a>.
          </p>
        </>
      ) : (
        <>
          <h3>Nothing for {page.audience} in this topic yet</h3>
          <p>
            Its articles are written for {other}.{" "}
            <a href={helpHref(page.path, other)}>See help for {other}</a>.
          </p>
        </>
      )}
      {page.popular.length > 0 && (
        <section className="help-suggested" aria-labelledby="help-suggested-title">
          <h3 id="help-suggested-title">Popular articles</h3>
          <ArticleList articles={page.popular} />
        </section>
      )}
    </div>
  );
}

export function HelpHome({ page }: { page: HelpIndex }) {
  const browse = !page.query && !page.category;
  return (
    <div className="help-center">
      <header className="help-hero">
        <div className="container">
          <p className="help-eyebrow">
            {browse ? (
              "Drate Help Center"
            ) : (
              <a href={helpHref("/help", page.audience)}>← All topics</a>
            )}
          </p>
          <h1>{page.category ? page.category.title : "How can we help?"}</h1>
          <p className="help-lede">
            {page.category
              ? page.category.description
              : "Answers for creators and the members who support them."}
          </p>
          <HelpSearch
            audience={page.audience}
            query={page.query}
            action={page.path}
            placeholder={page.category ? "Search this topic…" : undefined}
          />
          {browse && (
            <p className="help-popular-searches">
              <span>Popular:</span>
              {popularSearches[page.audience].map((search) => (
                <a key={search} href={helpHref("/help", page.audience, search)}>
                  {search}
                </a>
              ))}
              <a href={helpHref(aiPolicyPath, page.audience)}>AI policy</a>
            </p>
          )}
        </div>
      </header>
      <div className="container help-content">
        <div className="help-browse-bar">
          <div>
            <h2>{browse ? "Browse by topic" : page.query ? "Search results" : "Articles"}</h2>
            {!browse && (
              <p className="help-result-count">
                <span role="status">
                  {articles(page.results.length)}
                  {page.query
                    ? ` for “${page.query}”${page.category ? ` in ${page.category.title}` : ""}`
                    : ` for ${page.audience}`}
                </span>
                {page.query && <a href={helpHref(page.path, page.audience)}>Clear search</a>}
              </p>
            )}
          </div>
          <SegmentedLinks
            label="Help for"
            links={[
              {
                href: helpHref(page.path, "creators", page.query),
                label: "For creators",
                current: page.audience === "creators",
              },
              {
                href: helpHref(page.path, "members", page.query),
                label: "For members",
                current: page.audience === "members",
              },
            ]}
          />
        </div>
        {browse ? (
          <>
            <div className="help-topics">
              {page.categories.map((category) => {
                const Icon = icons[category.icon];
                const href = helpHref(`/help/topics/${category.slug}`, page.audience);
                return (
                  <section
                    className="help-topic"
                    key={category.slug}
                    aria-labelledby={`help-topic-${category.slug}`}
                  >
                    <Icon size={24} strokeWidth={1.5} aria-hidden="true" />
                    <h3 id={`help-topic-${category.slug}`}>
                      <a href={href}>{category.title}</a>
                    </h3>
                    <p>{category.description}</p>
                    <ul>
                      {category.articles.map((article) => (
                        <li key={article.slug}>
                          <a href={article.path}>{article.title}</a>
                        </li>
                      ))}
                    </ul>
                    {category.count > category.articles.length && (
                      <a className="help-all" href={href}>
                        All {articles(category.count)}
                        <ArrowRight size={16} aria-hidden="true" />
                      </a>
                    )}
                  </section>
                );
              })}
            </div>
            {page.popular.length > 0 && (
              <section className="help-popular" aria-labelledby="help-popular-title">
                <h2 id="help-popular-title">Popular articles</h2>
                <ArticleList articles={page.popular} columns />
              </section>
            )}
          </>
        ) : (
          <div className="help-results-layout">
            <div className="help-results">
              {page.results.length ? (
                <ArticleList articles={page.results} />
              ) : (
                <NoResults page={page} />
              )}
            </div>
            <TopicList page={page} />
          </div>
        )}
        <HelpContact />
      </div>
    </div>
  );
}

export function HelpArticle({ page }: { page: HelpArticlePage }) {
  const topic = helpHref(`/help/topics/${page.category.slug}`, page.audience);
  return (
    <div className="help-center">
      <div className="container help-article-top">
        <nav className="help-breadcrumbs" aria-label="Breadcrumb">
          <ol>
            <li>
              <a href={helpHref("/help", page.audience)}>Help center</a>
            </li>
            <li>
              <a href={topic}>{page.category.title}</a>
            </li>
          </ol>
        </nav>
        <HelpSearch audience={page.audience} compact />
      </div>
      {/* The title comes first at every width; the contents sit beside the body on wide screens
          and between the title and the body on narrow ones. */}
      <article className="container help-article">
        <header className="help-article-header">
          <h1>{page.title}</h1>
          <p className="help-article-description">{page.description}</p>
          <p className="help-date">
            Updated{" "}
            <time dateTime={page.updated}>
              {updatedOn.format(new Date(`${page.updated}T00:00:00Z`))}
            </time>
          </p>
        </header>
        {page.headings.length > 1 && (
          <aside className="help-aside">
            <nav className="help-side" aria-labelledby="help-contents-title">
              <p id="help-contents-title" className="help-side-label">
                In this article
              </p>
              <ul>
                {page.headings.map((heading) => (
                  <li key={heading.slug}>
                    <a href={`#${heading.slug}`}>{heading.text}</a>
                  </li>
                ))}
              </ul>
            </nav>
            <a className="help-back" href={topic}>
              More in {page.category.title}
            </a>
          </aside>
        )}
        {/* Trusted repository Markdown, rendered at build time; no customer content is injected. */}
        <div className="help-prose" dangerouslySetInnerHTML={{ __html: page.html }} />
        {page.related.length > 0 && (
          <section className="help-related" aria-labelledby="help-related-title">
            <h2 id="help-related-title">Related articles</h2>
            <ArticleList articles={page.related} />
          </section>
        )}
      </article>
      <div className="container help-article-end">
        <HelpContact />
      </div>
    </div>
  );
}
